import type { FC } from "react";
import { useMemo } from "react";

import type { PushBlockContentWrapperProps } from "@ui/components/PushBlockContentWrapper";
import { PushBlockContentWrapper } from "@ui/components/PushBlockContentWrapper";
import { useUser } from "@ui/hooks/useUser";

import { useAvailableSurveysStatus } from "@jti/rest-api";

import type { SmartSurveyContentProps } from "./components";
import { SmartSurveyContent } from "./components";
import { getIsUserEligibleForSurveyBasedOnDate, getSurveysByType } from "./utils";

import styles from "./SmartSurveyPushBlock.module.scss";

export type SmartSurveyPushBlockProps = PushBlockContentWrapperProps &
  SmartSurveyContentProps & {
    isInEditor?: boolean;
    dateOfUserRegistrationForSurvey?: string;
  };

export const SmartSurveyPushBlock: FC<SmartSurveyPushBlockProps> = ({
  alt,
  type,
  imgMobile,
  isInEditor,
  imgDesktop,
  orientation,
  dateOfUserRegistrationForSurvey,
  ...smartSurveyContentProps
}) => {
  const { user } = useUser();

  const {
    isSurveysStatusError,
    isSurveysStatusLoading,
    surveysStatus,
    refetch: refetchAvailableSurveys,
  } = useAvailableSurveysStatus(user?.uuid, user?.ciam_token);

  const isEligibleForSurvey = useMemo(
    () => getIsUserEligibleForSurveyBasedOnDate(user?.created_at, dateOfUserRegistrationForSurvey),
    [dateOfUserRegistrationForSurvey, user]
  );

  const { oneTimeSurveys, recurringSurveys } = useMemo(() => getSurveysByType(surveysStatus ?? []), [surveysStatus]);

  const oneTimeAvailableSurvey = useMemo(
    () => oneTimeSurveys.find((survey) => survey.status !== "completed"),
    [oneTimeSurveys]
  );

  const isOneTimeSurveyNotAvailable = !oneTimeAvailableSurvey && type === "one_time";

  const oneTimeSurveyInProgress = useMemo(() => {
    if (type === "one_time") {
      return oneTimeSurveys.find((survey) => survey.status === "in_progress");
    }
  }, [oneTimeSurveys, type]);

  const recurringAvailableSurvey = useMemo(
    () => recurringSurveys.find((survey) => survey.status !== "completed"),
    [recurringSurveys]
  );

  const isRecurringSurveyNotAvailable = type === "recurring" && !recurringAvailableSurvey;
  const isRecurringSurveyHidden = type === "recurring" && !recurringSurveys.length;

  const recurringSurveyInProgress = useMemo(() => {
    if (type === "recurring") {
      return recurringSurveys.find((survey) => survey.status === "in_progress");
    }
  }, [recurringSurveys, type]);

  const completedRecurringSurvey = useMemo(() => {
    if (type === "recurring") {
      return recurringSurveys.find((survey) => survey.status === "completed");
    }
  }, [recurringSurveys, type]);

  const inProgressSurvey = type === "one_time" ? oneTimeSurveyInProgress : recurringSurveyInProgress;
  const availableSurvey = type === "one_time" ? oneTimeAvailableSurvey : recurringAvailableSurvey;

  if (
    (isSurveysStatusError ||
      isSurveysStatusLoading ||
      isOneTimeSurveyNotAvailable ||
      !isEligibleForSurvey ||
      isRecurringSurveyHidden) &&
    !isInEditor
  ) {
    return null;
  }

  return (
    <PushBlockContentWrapper
      imgDesktop={imgDesktop}
      imgMobile={imgMobile}
      alt={alt}
      orientation={orientation}
      className={styles.smartSurveyPushBlock}
      renderContent={() => (
        <SmartSurveyContent
          {...smartSurveyContentProps}
          type={type}
          availableSurvey={availableSurvey}
          surveyInProgress={inProgressSurvey}
          isDisabled={isRecurringSurveyNotAvailable}
          onRefetchAvailableSurveys={refetchAvailableSurveys}
          completedRecurringSurvey={completedRecurringSurvey}
        />
      )}
    />
  );
};
